import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useCancellableCeleryPoll } from "../../hooks/useCancellableCeleryPoll";
import { uploadCollectionData } from "../../services/dataUploader";
import { applyCeleryPollOutcomeToAlert } from "../../utils/celeryPollAlert";
import FileInput from "./components/FileInput";
import { ALERT_MESSAGES, ALERT_TYPES } from "./constants/alertConstants";

/** Alert text for each terminal poll outcome (see applyCeleryPollOutcomeToAlert). */
const POLL_ALERT_COPY = {
  success: { severity: ALERT_TYPES.SUCCESS, headline: ALERT_MESSAGES.PROCESSING_SUCCESS },
  failed: { severity: ALERT_TYPES.ERROR, headline: ALERT_MESSAGES.PROCESSING_FAILED },
  timeout: { severity: ALERT_TYPES.WARNING, message: ALERT_MESSAGES.PROCESSING_TIMEOUT },
  fallback: { severity: ALERT_TYPES.ERROR, message: ALERT_MESSAGES.STATUS_CHECK_FAILED },
};

function DataUploader() {
  const [file, setFile] = useState(null);
  const [platformType, setPlatformType] = useState("Manual Collection");
  const { alertState, showAlert, closeAlert } = useAlert();
  const pollTask = useCancellableCeleryPoll(); // aborts on unmount / re-upload

  const handleUploadFile = async () => {
    if (!file) {
      showAlert(ALERT_TYPES.ERROR, ALERT_MESSAGES.NO_FILE);
      return;
    }

    showAlert(ALERT_TYPES.INFO, ALERT_MESSAGES.UPLOADING);

    const data = new FormData();
    data.append(platformType, file);

    try {
      // 1. Upload file; backend saves it and starts Celery with a task_id
      const uploadRes = await uploadCollectionData(data);
      if (!(uploadRes instanceof Response)) {
        return;
      }

      const uploadResJson = await uploadRes.json();

      if (!uploadRes.ok) {
        showAlert(ALERT_TYPES.ERROR, uploadResJson.error || "Upload failed");
        return;
      }

      const taskId = uploadResJson.task_id;
      if (!taskId) {
        showAlert(ALERT_TYPES.ERROR, ALERT_MESSAGES.STATUS_CHECK_FAILED);
        return;
      }

      // 2. Poll Mongo task log until completed / failed / timeout
      showAlert(ALERT_TYPES.INFO, ALERT_MESSAGES.PROCESSING);
      const { outcome, result } = await pollTask(taskId);
      applyCeleryPollOutcomeToAlert(showAlert, outcome, result, POLL_ALERT_COPY);
    } catch {
      showAlert(ALERT_TYPES.ERROR, ALERT_MESSAGES.STATUS_CHECK_FAILED);
    }
  };

  const handlePlatformChange = (event) => setPlatformType(event.target.value);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        <Grid size="grow">
          <Title text="Data Uploader" sx={{ my: 5 }} />
        </Grid>
      </TopBarGridContainer>

      <Grid container direction="column" alignItems="center" justifyContent="center" mt={5}>
        <Grid size={4} mb={5}>
          <FileInput file={file} setFile={setFile} />
        </Grid>
        <Grid size={4} mb={5}>
          <FormControl>
            <FormLabel id="platform-type-group-label">Platform Type</FormLabel>
            <RadioGroup
              row
              aria-labelledby="platform-type-group-label"
              name="radio-buttons-group"
              value={platformType}
              onChange={handlePlatformChange}
            >
              <FormControlLabel value="Manual Collection" control={<Radio />} label="Manual Collection" />
              <FormControlLabel value="Linkedin" control={<Radio />} label="Linkedin" />
              <FormControlLabel
                value="Manual Collection China"
                control={<Radio />}
                label="Manual Collection China"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid size="grow">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileUploadIcon />}
            sx={{ py: 1, ml: 1 }}
            onClick={handleUploadFile}
          >
            Upload File
          </Button>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default DataUploader;
